/* Sprint A — Hidden Research Competitor Insights Card.
 *
 * Renders the 6 sub-fields under `research.competitor_analysis.*`
 * that the Research agent has been GENERATING for many sprints but
 * the UI never displayed:
 *   - positioning[]           (per-competitor one-liner)
 *   - strengths_by_competitor[]
 *   - weaknesses_by_competitor[]
 *   - content_strategy_patterns[]
 *   - cta_patterns[]
 *   - posting_behaviour (string)
 *
 * Defensive — renders nothing when any sub-field is absent.
 */
import { TrendingUp, Shield, ShieldOff, Layers, Megaphone, Clock } from "lucide-react";

function asArray(v) { return Array.isArray(v) ? v : (v ? [v] : []); }

export default function ResearchCompetitorInsightsCard({ research }) {
    const ca = research?.competitor_analysis;
    if (!ca || typeof ca !== "object") return null;

    const positioning = asArray(ca.positioning);
    const strengths = asArray(ca.strengths_by_competitor);
    const weaknesses = asArray(ca.weaknesses_by_competitor);
    const csPatterns = asArray(ca.content_strategy_patterns);
    const ctaPatterns = asArray(ca.cta_patterns);
    const posting = ca.posting_behaviour;

    const anything = positioning.length || strengths.length || weaknesses.length
        || csPatterns.length || ctaPatterns.length || posting;
    if (!anything) return null;

    return (
        <div className="card-base p-5 mt-4" data-testid="research-competitor-insights">
            <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-zinc-400" />
                <div>
                    <div className="text-xs font-mono uppercase tracking-wider text-zinc-300">
                        Competitor Patterns (from Research)
                    </div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">
                        Cross-competitor patterns and positioning observed during research. URL-only — no engagement metrics fetched.
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {positioning.length ? (
                    <Block testid="rci-positioning" icon={Layers} title="Positioning (per competitor)">
                        <ul className="space-y-1.5">
                            {positioning.slice(0, 8).map((p, i) => (
                                <PositioningRow key={i} item={p} />
                            ))}
                        </ul>
                    </Block>
                ) : null}

                {strengths.length ? (
                    <Block testid="rci-strengths" icon={Shield} title="Strengths (across competitors)" tone="emerald">
                        <ul className="space-y-1.5">
                            {strengths.slice(0, 8).map((s, i) => (
                                <PatternRow key={i} item={s} />
                            ))}
                        </ul>
                    </Block>
                ) : null}

                {weaknesses.length ? (
                    <Block testid="rci-weaknesses" icon={ShieldOff} title="Weaknesses / Gaps" tone="red">
                        <ul className="space-y-1.5">
                            {weaknesses.slice(0, 8).map((w, i) => (
                                <PatternRow key={i} item={w} />
                            ))}
                        </ul>
                    </Block>
                ) : null}

                {csPatterns.length ? (
                    <Block testid="rci-content-strategy" icon={Layers} title="Content strategy patterns">
                        <ul className="space-y-1.5">
                            {csPatterns.slice(0, 8).map((c, i) => (
                                <PatternRow key={i} item={c} />
                            ))}
                        </ul>
                    </Block>
                ) : null}

                {ctaPatterns.length ? (
                    <Block testid="rci-cta-patterns" icon={Megaphone} title="CTA patterns">
                        <ul className="space-y-1.5">
                            {ctaPatterns.slice(0, 8).map((c, i) => (
                                <PatternRow key={i} item={c} />
                            ))}
                        </ul>
                    </Block>
                ) : null}

                {posting ? (
                    <Block testid="rci-posting-behaviour" icon={Clock} title="Posting behaviour">
                        <div className="text-[12px] text-zinc-300 leading-relaxed">
                            {typeof posting === "string" ? posting : JSON.stringify(posting)}
                        </div>
                    </Block>
                ) : null}
            </div>
        </div>
    );
}

function PositioningRow({ item }) {
    if (typeof item === "string") {
        return <li className="text-[12px] text-zinc-300 leading-snug">{item}</li>;
    }
    return (
        <li className="text-[12px] text-zinc-300 leading-snug">
            <span className="font-mono text-zinc-100">{item.competitor || item.name || "—"}:</span>{" "}
            {item.positioning || item.statement || item.summary || JSON.stringify(item)}
        </li>
    );
}

function PatternRow({ item }) {
    if (typeof item === "string") {
        return <li className="text-[12px] text-zinc-300 leading-snug">• {item}</li>;
    }
    // Common shapes: {competitor, strength}, {competitor, weakness}, {pattern, ...}
    const label = item.competitor || item.name;
    const body = item.strength || item.weakness || item.pattern
        || item.observation || item.insight || item.cta || item.summary
        || JSON.stringify(item);
    return (
        <li className="text-[12px] text-zinc-300 leading-snug">
            • {label ? <span className="font-mono text-zinc-100">{label}: </span> : null}
            {body}
        </li>
    );
}

function Block({ icon: Icon, title, children, tone, testid }) {
    const toneClass = tone === "emerald"
        ? "border-emerald-500/20"
        : tone === "red"
            ? "border-red-500/20"
            : "border-zinc-800";
    return (
        <div className={"border rounded-sm p-3 " + toneClass} data-testid={testid}>
            <div className="flex items-center gap-1.5 mb-2">
                <Icon className="w-3 h-3 text-zinc-500" />
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">{title}</div>
            </div>
            {children}
        </div>
    );
}
